import { useContext } from 'react';
import { SessionContext, type Role, type SessionValue } from './sessionContext';

/**
 * Sessão corrente (usuário, papel, tenant e estado). Exige `<SessionProvider>`
 * acima — o que vale para todo o console e para `renderApp` nos testes.
 */
export function useCurrentUser(): SessionValue {
  const sessao = useContext(SessionContext);
  if (!sessao) throw new Error('useCurrentUser: falta um <SessionProvider> acima na árvore.');
  return sessao;
}

/**
 * `true` quando o papel da sessão está entre os aceitos.
 *
 * Papel desconhecido (carregando ou `getMe` indisponível) responde `true`: a
 * interface não esconde nada por não saber quem é — o servidor recusa a ação
 * (FR-009) e é ele quem manda.
 */
export function useHasRole(...roleList: Role[]): boolean {
  const { papel } = useCurrentUser();
  return hasRole(papel, roleList);
}

/**
 * Versão pura de `useTemPapel`.
 *
 * `padraoSemPapel` decide o que fazer quando **não sabemos** quem é (sessão
 * carregando ou `getMe` indisponível), e os dois eixos do console querem coisas
 * diferentes:
 *
 * - **guarda de rota** (`true`, padrão): mostrar demais na dúvida não escala
 *   privilégio — o FR-009 declara que o papel é verificado no servidor
 *   "independentemente do que a interface exibe". Falhar fechado trancaria um
 *   admin legítimo sempre que o `getMe` degradasse, sem ganho nenhum.
 * - **menu** (`false`, ver `railGroups`): o FR-010 manda **omitir** o item;
 *   mostrá-lo leva o usuário a uma tela que responderá 403 — recusa torta em vez
 *   de recusa honesta. Quem tem o papel ainda chega pela URL, porque a guarda
 *   passa e o backend autoriza.
 */
export function hasRole(
  papel: Role | null,
  roleList?: readonly Role[],
  padraoSemPapel = true,
): boolean {
  if (!roleList?.length) return true;
  if (!papel) return padraoSemPapel;
  return roleList.includes(papel);
}
